import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ProductsAPI } from '../services/api';
import { useCart } from '../contexts/CartContext';

const ProductDetail = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const { dispatch } = useCart();

  useEffect(() => {
    const fetchProductDetail = async () => {
      try {
        setLoading(true);
        const response = await ProductsAPI.getProduct(id);
        // Извличане на продукта от отговора
        setProduct(response.product);
        setLoading(false);
      } catch (err) {
        setError('Неуспешно зареждане на детайли за продукта. Моля, опитайте отново по-късно.');
        setLoading(false);
      }
    };

    fetchProductDetail();
  }, [id]);

  const handleQuantityChange = (e) => {
    const value = parseInt(e.target.value);
    setQuantity(value > 0 ? value : 1);
  };

  const handleAddToCart = () => {
    if (product) {
      dispatch({
        type: 'ADD_ITEM',
        payload: {
          id: product.id,
          name: product.name,
          price: parseFloat(product.price),
          image: product.images && product.images.length > 0 ? product.images[0].src : '',
          quantity: quantity
        }
      });
    }
  };

  const goToPreviousImage = () => {
    if (!product?.images?.length) return;
    setSelectedImageIndex((prevIndex) => 
      prevIndex === 0 ? product.images.length - 1 : prevIndex - 1
    );
  };

  const goToNextImage = () => {
    if (!product?.images?.length) return;
    setSelectedImageIndex((prevIndex) => 
      prevIndex === product.images.length - 1 ? 0 : prevIndex + 1
    );
  };

  if (loading) return <div className="text-center py-10 text-lg">Зареждане на детайли за продукта...</div>;
  if (error) return <div className="text-center py-10 text-lg text-red-600">{error}</div>;
  if (!product) return <div className="text-center py-10 text-lg">Продуктът не е намерен</div>;

  const isInStock = product.stock_status === 'instock';
  const hasMultipleImages = product.images && product.images.length > 1;

  return (
    <div className="max-w-7xl mx-auto px-5 py-8">
      <div className="mb-4">
        <Link to="/products" className="text-primary hover:underline">
          ← Обратно към продуктите
        </Link>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="space-y-4">
          <div className="bg-gray-50 rounded-lg overflow-hidden p-4 flex items-center justify-center h-[400px] relative">
            {product.images && product.images.length > 0 ? (
              <>
                <img 
                  src={product.images[selectedImageIndex].src} 
                  alt={`${product.name} - Изглед ${selectedImageIndex + 1}`} 
                  className="w-full h-full max-h-[400px] object-contain"
                />
                
                {hasMultipleImages && (
                  <>
                    <button 
                      onClick={goToPreviousImage}
                      className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white w-10 h-10 rounded-full flex items-center justify-center shadow-md"
                      aria-label="Предишно изображение"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                    
                    <button 
                      onClick={goToNextImage}
                      className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white w-10 h-10 rounded-full flex items-center justify-center shadow-md"
                      aria-label="Следващо изображение"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </>
                )}
              </>
            ) : (
              <div className="text-gray-400 text-lg p-20">Няма налично изображение</div>
            )}
          </div>
          
          {hasMultipleImages && (
            <div className="flex overflow-x-auto space-x-2 py-2">
              {product.images.map((image, index) => (
                <div 
                  key={index}
                  onClick={() => setSelectedImageIndex(index)}
                  className={`cursor-pointer border-2 rounded-md overflow-hidden w-20 h-20 flex-shrink-0 
                    ${selectedImageIndex === index ? 'border-primary' : 'border-transparent hover:border-gray-300'}`}
                >
                  <img 
                    src={image.src} 
                    alt={`${product.name} миниатюра ${index + 1}`} 
                    className="w-full h-full object-cover"
                  />
                </div>
              ))}
            </div>
          )}
          
          {hasMultipleImages && (
            <div className="text-center text-sm text-gray-500">
              Изображение {selectedImageIndex + 1} от {product.images.length}
            </div>
          )}
        </div>
        
        <div>
          <h1 className="text-3xl font-bold mb-4">{product.name}</h1>
          
          <div className="text-2xl font-bold text-gray-900 mb-4">
            {parseFloat(product.price).toFixed(2)} лв
          </div>
          
          <div className="mb-4">
            <span className={`${isInStock ? 'text-green-600' : 'text-red-600'}`}>
              {isInStock ? 'В наличност' : 'Изчерпан'}
            </span>
          </div>
          
          <div className="mb-6 prose" dangerouslySetInnerHTML={{ __html: product.description }} />
          
          <div className="flex flex-col space-y-4">
            <div className="flex items-center">
              <label htmlFor="quantity" className="mr-4 font-medium">Количество:</label>
              <input
                type="number"
                id="quantity"
                name="quantity"
                min="1"
                value={quantity}
                onChange={handleQuantityChange}
                className="w-20 px-3 py-2 border border-gray-300 rounded-md text-center"
                disabled={!isInStock}
              />
            </div>
            
            <button
              onClick={handleAddToCart}
              disabled={!isInStock}
              className={`py-3 px-6 rounded-md font-medium text-white transition ${
                isInStock ? 'bg-primary hover:bg-primary/80' : 'bg-gray-400 cursor-not-allowed'
              }`}
            >
              {isInStock ? 'Добави в кошницата' : 'Изчерпан'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail; 